import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram, ChevronLeft, ChevronRight } from 'lucide-react';
import Lottie from 'lottie-react';
import { fetchArtist, fetchProductsByArtist, contactArtist } from '../services/artistsService';
import { trackArtistContact } from '../services/analyticsService';
import { CACHE_KEYS } from '../services/kioskCache';
import useKioskData from '../hooks/useKioskData';
import OfflineBanner from '../components/OfflineBanner';
import loadingAnimation from '../assets/loading.json';
import ArtistGalleryFigure from '../components/ArtistGalleryFigure';
import useScrollReveal from '../hooks/useScrollReveal';
import usePageMeta from '../hooks/usePageMeta';
import { useLocale } from '../hooks/useLocale';
import { formatDate as fmtDate, formatPrice } from '../utils/format';
import { tField } from '../utils/i18nContent';
import { heroShowcase, productCard, productDetail } from '../lib/imageProxy';
import BlurImage from '../components/BlurImage';
import './ArtistPage.css';

const ACCENT_PALETTE = [
  '#1f2937', '#5b3a29', '#3b3a4d', '#264653', '#4a3b52',
  '#2f3e2e', '#3c2a4d', '#4a2e2e', '#1c3a47', '#3a3a1f'
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const heroFgVars = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5
    ? { '--hero-fg': 'rgba(0,0,0,0.87)',   '--hero-fg-muted': '#000000' }
    : { '--hero-fg': 'rgba(255,255,255,0.92)', '--hero-fg-muted': '#ffffff' };
};

const pickAccentColor = (artist) => {
  if (artist?.accent_color && HEX_RE.test(artist.accent_color)) {
    return artist.accent_color;
  }
  const seed = (artist?.artist_name || artist?.name || artist?.slug || '').toString();
  if (!seed) return ACCENT_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return ACCENT_PALETTE[hash % ACCENT_PALETTE.length];
};

const groupBySeries = (products) => {
  const groups = new Map();
  products.forEach((p) => {
    const key = p.series && p.series !== 'N/A' ? p.series : '__other__';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  });
  return Array.from(groups.entries()).map(([series, items]) => ({ series, items }));
};

const RevealSection = ({ as: Tag = 'section', className = '', children, ...rest }) => {
  const [ref, visible] = useScrollReveal();
  return (
    <Tag
      ref={ref}
      className={`${className} artist-reveal ${visible ? 'is-visible' : ''}`}
      {...rest}
    >
      {children}
    </Tag>
  );
};

const ContactSection = ({ identifier, artistId, artistName }) => {
  const { t } = useTranslation('artist');
  const method = 'email';
  const [name, setName]       = useState('');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus]   = useState('idle');
  const [errorMsg, setErrorMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg(null);
    try {
      await contactArtist(identifier, {
        name,
        contactMethod: method,
        contactValue: contact,
        message,
      });
      setStatus('success');
      trackArtistContact(artistId, identifier, method);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || t('page.contact.error'));
    }
  };

  return (
    <RevealSection className="artist-contact-section" aria-label={t('page.contact.sectionLabel')}>
      <div className="artist-contact-inner">
        <div className="artist-contact-copy">
          <h2 className="artist-contact-heading">
            {t('page.contact.headingPre')}{' '}
            <em>{artistName}</em>
          </h2>
          <p className="artist-contact-body">{t('page.contact.prompt')}</p>
        </div>

        <div className="artist-contact-card">
          {status === 'success' ? (
            <p className="artist-contact-success">{t('page.contact.success')}</p>
          ) : (
            <form className="artist-contact-form" onSubmit={handleSubmit}>
              <label className="artist-contact-label">
                {t('page.contact.nameLabel')}
                <input
                  type="text"
                  required
                  autoComplete="name"
                  placeholder={t('page.contact.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={status === 'submitting'}
                  className="artist-contact-input"
                />
              </label>

              <label className="artist-contact-label">
                {t('page.contact.emailLabel')}
                <input
                  type="email"
                  required
                  autoComplete="email"
                  placeholder={t('page.contact.emailPlaceholder')}
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  disabled={status === 'submitting'}
                  className="artist-contact-input"
                />
              </label>

              <label className="artist-contact-label">
                {t('page.contact.messageLabel')}
                <textarea
                  placeholder={t('page.contact.messagePlaceholder')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={status === 'submitting'}
                  className="artist-contact-textarea"
                  rows={3}
                  maxLength={500}
                />
              </label>

              {status === 'error' && errorMsg && (
                <p className="artist-contact-error">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting' || !name.trim() || !contact.trim()}
                className="artist-contact-submit"
              >
                {status === 'submitting' ? t('page.contact.submitting') : t('page.contact.submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </RevealSection>
  );
};

const StudioVideoSection = ({ videoUrl, posterUrl, artistName, caption }) => {
  const { t } = useTranslation('artist');
  const videoRef = useRef(null);
  // iOS Safari only honors muted+playsinline autoplay reliably when the
  // native `autoplay` attribute is present on the tag itself — kicking off
  // playback imperatively via node.play() on mount (as this used to do) is
  // racy there: if the resource hasn't buffered enough yet the call can be
  // silently dropped, leaving the poster frame (the first showroom image)
  // frozen on screen instead of the video. Read the initial preference once
  // so the attribute isn't present at all for reduced-motion users (avoids a
  // one-frame autoplay flash before the effect below pauses it).
  const [autoPlayAllowed] = useState(() => (
    typeof window === 'undefined' || !window.matchMedia
      ? true
      : !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ));

  useEffect(() => {
    const node = videoRef.current;
    if (!node) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Only reacts to the OS setting changing *after* mount — the initial
    // play/pause state is handled by the autoPlay attribute above.
    const apply = () => {
      if (mq.matches) {
        node.pause();
      } else {
        const playPromise = node.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => { /* autoplay can be blocked; ignore */ });
        }
      }
    };
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  // Studio body text: artist-provided value overrides the generic i18n
  // template ("Un vistazo al proceso detrás de las piezas de X.") so each
  // artist can write their own one-liner. Falls back to the template when
  // the field is empty so existing artists don't suddenly lose copy.
  const bodyText = caption?.trim() || t('page.studio.body', { name: artistName });

  return (
    <RevealSection className="artist-studio" aria-label={t('page.studio.sectionLabel')}>
      <div className="artist-studio-inner">
        <div className="artist-studio-copy">
          <h2 className="artist-section-heading">{t('page.studio.heading')}</h2>
          <p className="artist-studio-text">{bodyText}</p>
        </div>
        <figure className="artist-studio-figure">
          <video
            ref={videoRef}
            className="artist-studio-video"
            src={videoUrl}
            poster={posterUrl || undefined}
            muted
            loop
            playsInline
            autoPlay={autoPlayAllowed}
            preload="auto"
            aria-label={t('page.studio.videoLabel', { name: artistName })}
          />
        </figure>
      </div>
    </RevealSection>
  );
};

// Localized month name ("Junio") for the date spine — month is optional
// (added after year shipped), so most existing entries won't have one; the
// year itself renders directly from entry.year beside it.
const formatEntryMonth = (entry, locale) => {
  if (!entry.year || !entry.month) return null;
  const date = new Date(Date.UTC(entry.year, entry.month - 1, 1));
  return fmtDate(date, locale, { month: 'long', timeZone: 'UTC' });
};

// A single custom-show entry, rendered as its photo(s) used as the card's
// own background. By default the card shows the full, clear photo (with
// arrows/dots to browse multiple shots); tapping it blurs the photo and
// reveals the story/specs over it like a museum wall label — no separate
// lightbox. 1–4 photos crossfade through the same interaction as the
// product detail gallery (see ProductPage.js). The story/specs view also
// rides the same carousel as the photos, as one extra slide past the last
// image, so it's reachable by arrow/dot navigation with no tap required.
const CustomHistoryCard = ({ images, alt, title, subtitle, story, specs }) => {
  const { t } = useTranslation('artist');
  const [index, setIndex] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const count = images.length;
  const activeIndex = index < count ? index : 0;
  const hasInfo = Boolean(story) || specs.length > 0;
  // Slide positions 0..count-1 are photos; slide `count` (only present when
  // there's something to show) is the plaque.
  const slideCount = count + (hasInfo ? 1 : 0);
  const activeSlide = detailsOpen ? count : activeIndex;

  const goToSlide = (slide) => {
    if (slide >= count) {
      setDetailsOpen(true);
    } else {
      setDetailsOpen(false);
      setIndex(slide);
    }
  };
  const step = (delta) => goToSlide(((activeSlide + delta) % slideCount + slideCount) % slideCount);

  return (
    <div className={`artist-custom-history-card${detailsOpen ? ' is-revealed' : ''}`}>
      <div className="artist-custom-history-photo">
        {images.map((img, i) => (
          <div
            key={i}
            className="artist-custom-history-photo-slide"
            aria-hidden={i !== activeIndex}
            style={{
              opacity: i === activeIndex ? 1 : 0,
              pointerEvents: i === activeIndex ? 'auto' : 'none',
            }}
          >
            <BlurImage
              className="artist-custom-history-photo-img"
              src={productDetail(img)}
              alt={alt}
              loading="lazy"
              objectFit="cover"
              // BlurImage's own inline style otherwise wins the cascade over
              // the stylesheet's transition (it doesn't list `filter`), so
              // the reveal/blur toggle would snap instead of animating.
              style={{ transition: 'filter 0.5s ease, transform 0.5s ease' }}
            />
          </div>
        ))}
      </div>

      {/* Full-card hit target — tapping the photo blurs it and reveals the
          story/specs in place instead of opening a lightbox. */}
      <button
        type="button"
        className="artist-custom-history-card-hit"
        onClick={() => setDetailsOpen((open) => !open)}
        aria-label={detailsOpen ? t('page.customHistory.hideDetails') : t('page.customHistory.viewDetails')}
      />

      <div className="artist-custom-history-card-title">
        {title && <h3 className="artist-custom-history-name">{title}</h3>}
        {subtitle && <span className="artist-custom-history-subtitle">{subtitle}</span>}
      </div>

      {hasInfo && (
        <div className="artist-custom-history-card-info" aria-hidden={!detailsOpen}>
          {story && <p className="artist-custom-history-story">{story}</p>}
          {specs.length > 0 && (
            <dl className="artist-custom-history-specs">
              {specs.map(({ key, label, value, variant }) => (
                <div
                  key={key}
                  className={`artist-custom-history-spec${variant ? ` artist-custom-history-spec--${variant}` : ''}`}
                >
                  <dt className="artist-custom-history-spec-label">{label}</dt>
                  <dd className="artist-custom-history-spec-value">{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}

      {slideCount > 1 && (
        <>
          <button
            type="button"
            className="artist-custom-history-carousel-nav artist-custom-history-carousel-nav--prev"
            onClick={() => step(-1)}
            aria-label={t('page.customHistory.prevImage')}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            className="artist-custom-history-carousel-nav artist-custom-history-carousel-nav--next"
            onClick={() => step(1)}
            aria-label={t('page.customHistory.nextImage')}
          >
            <ChevronRight size={18} />
          </button>
          <div className="artist-custom-history-carousel-dots">
            {Array.from({ length: slideCount }, (_, i) => (
              <button
                key={i}
                type="button"
                className={`artist-custom-history-carousel-dot${i === activeSlide ? ' is-active' : ''}${i === count ? ' artist-custom-history-carousel-dot--story' : ''}`}
                onClick={() => goToSlide(i)}
                aria-current={i === activeSlide}
                aria-label={i === count ? t('page.customHistory.goToStory') : t('page.customHistory.goToImage', { number: i + 1 })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CustomHistorySection = ({ entries, locale }) => {
  const { t } = useTranslation('artist');

  return (
    <RevealSection className="artist-custom-history" aria-label={t('page.customHistory.sectionLabel')}>
      <h2 className="artist-section-heading">{t('page.customHistory.heading')}</h2>
      <ol className="artist-custom-history-list">
        {entries.map((entry) => {
          // Free-text fields carry optional `${field}_en/_zh/_ja` translations
          // (see api/_customShowHistory.js) — read through tField() like every
          // other bilingual DB column, falling back to the Spanish original.
          // eventName is collected in the admin form but no longer shown on
          // this page — customName/platform carry the card's title/subtitle.
          const customName = tField(entry, 'customName', locale);
          const platform = tField(entry, 'platform', locale);
          const platformCreator = tField(entry, 'platformCreator', locale);
          const story = tField(entry, 'story', locale);
          const materials = tField(entry, 'materials', locale);
          const dimensions = tField(entry, 'dimensions', locale);
          const month = formatEntryMonth(entry, locale);
          // Up to 4 photos, shown as a carousel in the image space.
          // Falls back to the legacy single `image` key for older entries.
          const images = (Array.isArray(entry.images) && entry.images.length > 0
            ? entry.images
            : (entry.image ? [entry.image] : [])
          ).filter(Boolean).slice(0, 4);

          const specs = [
            { key: 'platform', label: t('page.customHistory.platformLabel'), value: platform },
            { key: 'materials', label: t('page.customHistory.materialsLabel'), value: materials },
            // Placed right after materials so the 2-column spec grid lands it
            // directly below the platform value (row 2, column 1).
            platformCreator && { key: 'platformCreator', label: t('page.customHistory.platformCreatorLabel'), value: platformCreator, variant: 'platform-creator' },
            dimensions && { key: 'dimensions', label: t('page.customHistory.dimensionsLabel'), value: dimensions, variant: 'dimensions' },
            // Sold-out pieces don't show a price — nothing left to buy at it.
            entry.price != null && !entry.soldOut && {
              key: 'price',
              label: t('page.customHistory.priceLabel'),
              value: formatPrice(entry.price, 'MXN', locale),
              variant: 'price',
            },
          ].filter(Boolean);

          return (
            <li key={entry.id} className="artist-custom-history-item">
              <div className="artist-custom-history-spine">
                {entry.year && (
                  <div className="artist-custom-history-date">
                    <span className="artist-custom-history-year">{entry.year}</span>
                    {month && <span className="artist-custom-history-month">{month}</span>}
                  </div>
                )}
              </div>
              {images.length > 0 && (
                <CustomHistoryCard
                  images={images}
                  alt={t('page.customHistory.imageAlt', { name: customName || platform })}
                  title={customName || null}
                  subtitle={platform || null}
                  story={story}
                  specs={specs}
                />
              )}
            </li>
          );
        })}
      </ol>
    </RevealSection>
  );
};

const ArtistPage = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const locale = useLocale();
  const { t } = useTranslation('artist');
  const [activeDot, setActiveDot] = useState(0);

  const {
    data: pageData,
    loading,
    error,
    fromCache,
  } = useKioskData(
    CACHE_KEYS.artist(identifier),
    async () => {
      const artistData = await fetchArtist(identifier);
      if (!artistData) throw new Error(t('page.notFound'));
      const productList = await fetchProductsByArtist(artistData);
      return { artist: artistData, products: productList };
    },
    { enabled: !!identifier }
  );

  const artist = pageData?.artist ?? null;
  const products = useMemo(() => pageData?.products ?? [], [pageData]);


  const accentColor = useMemo(() => pickAccentColor(artist), [artist]);
  const accentFgVars = useMemo(() => {
    if (artist?.font_color && HEX_RE.test(artist.font_color)) {
      return { '--hero-fg': artist.font_color, '--hero-fg-muted': artist.font_color };
    }
    return heroFgVars(accentColor);
  }, [accentColor, artist]);
  const bioText = artist ? (tField(artist, 'about', locale) || '') : '';
  const heroQuoteText = artist ? (tField(artist, 'manifesto', locale) || null) : null;
  const heroRightsText = !heroQuoteText
    ? (artist ? (tField(artist, 'rights_reserved', locale) || null) : null)
    : null;

  // Series chapters layout: when the artist has at least two series with
  // three or more pieces each. Otherwise flat mosaic.
  const seriesChapters = useMemo(() => {
    const grouped = groupBySeries(products);
    const meaningful = grouped.filter(
      (g) => g.series !== '__other__' && g.items.length >= 3
    );
    if (meaningful.length < 2) return null;
    const other = grouped.find((g) => g.series === '__other__');
    return other && other.items.length > 0 ? [...meaningful, other] : meaningful;
  }, [products]);

  const datedDrops = useMemo(() => {
    return products
      .filter((p) => p.release_date)
      .map((p) => ({ ...p, _date: new Date(p.release_date) }))
      .sort((a, b) => b._date - a._date);
  }, [products]);

  const customHistory = useMemo(() => {
    const entries = Array.isArray(artist?.custom_show_history) ? artist.custom_show_history : [];
    // Sort by year desc, then month desc within a year — entries missing a
    // month (added before month support, or left unset) sort as if they
    // were month 0, i.e. after dated entries from the same year. Compares
    // year and month separately (rather than folding them into one number)
    // so a year alone always outranks every entry from the prior year,
    // regardless of month.
    return entries
      .filter((e) => e && e.image)
      .slice()
      .sort((a, b) => (b.year || 0) - (a.year || 0) || (b.month || 0) - (a.month || 0));
  }, [artist]);

  const showcaseImages = [
    artist?.showcase_image_1_url,
    artist?.showcase_image_2_url,
    artist?.showcase_image_3_url
  ].filter(Boolean);

  const heroFallbackImage = heroShowcase(
    artist?.cover_image_url || products[0]?.image || artist?.profile_image_url
  );

  const handleShowcaseScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (showcaseImages.length < 2) return;
    const snapStep = (el.scrollWidth - el.clientWidth) / (showcaseImages.length - 1);
    if (snapStep <= 0) return;
    setActiveDot(Math.min(showcaseImages.length - 1, Math.max(0, Math.round(el.scrollLeft / snapStep))));
  }, [showcaseImages.length]);

  const displayName = artist?.artist_name || artist?.name || '';
  const creatorName = artist?.creator_name || null;
  const creatorAbout = artist ? (tField(artist, 'creator_about', locale) || null) : null;
  const creatorProfileImageUrl = artist?.creator_profile_image_url || null;
  const showCreatorFirst = Boolean(artist?.creator_about_first);
  // The hero's headline name — the artist's own name when they have priority
  // (creatorAboutFirst), the project/brand name otherwise. Everywhere else
  // on the page (SEO title, image alts, gallery, etc.) keeps using the
  // project name — only the hero headline switches.
  const heroName = (showCreatorFirst && creatorName) ? creatorName : displayName;
  usePageMeta({
    title: displayName ? `${displayName} — Bluppimart` : 'Artista — Bluppimart',
    description: (heroQuoteText || bioText || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 200) || undefined,
    image: heroShowcase(artist?.showcase_image_1_url || artist?.cover_image_url || artist?.profile_image_url) || undefined,
    type: 'profile',
  });
  const processImages = Array.isArray(artist?.process_images)
    ? artist.process_images.filter(Boolean)
    : [];
  const signatureSvg = artist?.signature_svg_url || null;

  if (loading) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <Lottie
          animationData={loadingAnimation}
          loop
          style={{ width: 200, height: 200, margin: '0 auto' }}
        />
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <p style={{ color: '#dc3545', marginBottom: '1rem', fontSize: '1.2rem' }}>
          {error || t('page.notFound')}
        </p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          {t('page.backHome')}
        </button>
      </div>
    );
  }

  return (
    <article className="artist-page" style={{ '--accent': accentColor, ...accentFgVars }}>
      <OfflineBanner show={fromCache} />
      {/* 3.1 Hero — "Living poster" */}
      <section
        className="artist-hero"
        style={{ backgroundColor: accentColor }}
        aria-label={t('page.showroomLabel', { name: heroName })}
      >
        <div className="artist-hero-motion" aria-hidden="true" />
        <h1 className="artist-hero-name">{heroName}</h1>

        {showcaseImages.length > 0 ? (
          <div className="artist-hero-showcase-wrap">
            <div
              className="artist-hero-showcase"
              data-count={showcaseImages.length}
              onScroll={handleShowcaseScroll}
            >
              {showcaseImages.map((src, i) => (
                <div key={`${src}-${i}`} className="artist-hero-showcase-slot">
                  <img
                    className="artist-hero-showcase-piece"
                    src={heroShowcase(src)}
                    alt={t('page.showcasePieceAlt', { n: i + 1, name: displayName })}
                    onLoad={(e) => {
                      e.currentTarget.classList.add('is-loaded');
                      e.currentTarget.parentElement.classList.add('is-loaded');
                    }}
                    onError={(e) => {
                      e.currentTarget.style.visibility = 'hidden';
                    }}
                  />
                </div>
              ))}
            </div>
            {showcaseImages.length > 1 && (
              <div className="artist-hero-showcase-dots" aria-hidden="true">
                {showcaseImages.map((_, i) => (
                  <span
                    key={i}
                    className={`artist-hero-showcase-dot${i === activeDot ? ' is-active' : ''}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : heroFallbackImage ? (
          <img
            className="artist-hero-piece"
            src={heroFallbackImage}
            alt={t('page.featuredImageAlt', { name: displayName })}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : null}

        {artist?.instagram_handle && (
          <a
            className="artist-hero-instagram artist-hero-instagram--below"
            href={`https://www.instagram.com/${artist.instagram_handle}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Instagram de ${displayName}: @${artist.instagram_handle}`}
          >
            <Instagram size={18} aria-hidden="true" />
            {artist.instagram_handle}
          </a>
        )}

        <div className="artist-hero-caption">
          {heroName}
          {/* Hidden when the creator bio leads (creatorAboutFirst) — the
              hero headline already is the artist's name at that point, so a
              "por {creator}" byline would be redundant; the project gets its
              own heading further down instead. */}
          {creatorName && !showCreatorFirst && (
            <span className="artist-hero-by">{t('page.byCreator', { name: creatorName })}</span>
          )}
        </div>

        {(heroQuoteText || heroRightsText || artist.instagram_handle) && (
          <div className="artist-hero-overlay-right">
            {heroQuoteText && (
              <blockquote className="artist-hero-quote">"{heroQuoteText}"</blockquote>
            )}
            {heroRightsText && (
              <p className="artist-hero-rights">{heroRightsText}</p>
            )}
            {artist.instagram_handle && (
              <a
                className="artist-hero-instagram artist-hero-instagram--overlay"
                href={`https://www.instagram.com/${artist.instagram_handle}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Instagram de ${displayName}: @${artist.instagram_handle}`}
              >
                <Instagram size={18} aria-hidden="true" />
                {artist.instagram_handle}
              </a>
            )}
          </div>
        )}
      </section>

      {/* 3.2 Manifesto / bio */}
      {(bioText || creatorAbout) && (
        <RevealSection className="artist-bio" aria-label={t('page.bioSection.sectionLabel')}>
          <div className="artist-bio-inner">
            {(() => {
              // When the entry is a brand/project (creator_name set), the
              // project heading frames the brand description with the brand
              // name; the creator gets a separate heading. Which one leads
              // is admin-controlled (creatorAboutFirst) — the leading block
              // gets <h2> and no top margin, the trailing one <h3>.
              const ProjectHeadingTag = showCreatorFirst ? 'h3' : 'h2';
              const CreatorHeadingTag = showCreatorFirst ? 'h2' : 'h3';

              const projectHeadingText = creatorName
                ? t('page.bioSection.projectHeading', { name: displayName })
                : t('page.bioSection.heading');

              const projectBlock = bioText && (
                <>
                  <ProjectHeadingTag className="artist-section-heading">
                    {projectHeadingText}
                  </ProjectHeadingTag>
                  {artist.profile_image_url && (
                    <figure className="artist-bio-portrait">
                      <BlurImage
                        src={productCard(artist.profile_image_url)}
                        alt={t('page.bioSection.portraitAlt', { name: displayName })}
                        loading="lazy"
                        objectFit="cover"
                        onError={(e) => {
                          e.currentTarget.closest('figure').style.display = 'none';
                        }}
                      />
                    </figure>
                  )}
                  <p className="artist-bio-body">{bioText}</p>
                </>
              );

              const creatorBlock = creatorAbout && (
                <>
                  <CreatorHeadingTag
                    className={`artist-bio-creator-heading${showCreatorFirst ? ' artist-bio-creator-heading--first' : ''}`}
                  >
                    {creatorName
                      ? t('page.bioSection.creatorHeading', { name: creatorName })
                      : t('page.bioSection.creatorHeadingGeneric')}
                  </CreatorHeadingTag>
                  <div className="artist-bio-creator">
                    {creatorProfileImageUrl && (
                      <figure className="artist-bio-creator-portrait">
                        <BlurImage
                          src={productCard(creatorProfileImageUrl)}
                          alt={creatorName
                            ? t('page.bioSection.portraitAlt', { name: creatorName })
                            : t('page.bioSection.creatorPortraitAltGeneric')}
                          loading="lazy"
                          objectFit="cover"
                          onError={(e) => {
                            e.currentTarget.closest('figure').style.display = 'none';
                          }}
                        />
                      </figure>
                    )}
                    <p className="artist-bio-body artist-bio-body--creator">{creatorAbout}</p>
                  </div>
                </>
              );

              return showCreatorFirst
                ? <>{creatorBlock}{projectBlock}</>
                : <>{projectBlock}{creatorBlock}</>;
            })()}
            {signatureSvg && (
              <img
                className="artist-bio-signature"
                src={signatureSvg}
                alt={t('page.bioSection.signatureAlt', { name: displayName })}
              />
            )}
          </div>
        </RevealSection>
      )}

      {/* 3.2b Custom show history — optional timeline, gated by data */}
      {customHistory.length > 0 && (
        <CustomHistorySection entries={customHistory} locale={locale} />
      )}

      {/* 3.5 En el estudio — vertical studio video, gated by data */}
      {artist.studio_video_url && (
        <StudioVideoSection
          videoUrl={artist.studio_video_url}
          posterUrl={showcaseImages[0] || artist.cover_image_url}
          artistName={displayName}
          caption={tField(artist, 'studio_video_caption', locale) || null}
        />
      )}

      {/* 3.3 Mosaic gallery — hidden when the artist has no products */}
      {products.length > 0 && (
      <RevealSection className="artist-gallery" aria-label={t('page.gallery.sectionLabel')}>
        <header className="artist-gallery-header">
          <h2 className="artist-section-heading">{tField(artist, 'gallery_heading', locale) || t('page.gallery.heading')}</h2>
        </header>

        {seriesChapters ? (
          seriesChapters.map(({ series, items }) => (
            <div key={series} className="artist-gallery-chapter">
              <h3 className="artist-gallery-chapter-title">
                {series === '__other__' ? t('page.gallery.otherWorks') : t('page.gallery.series', { name: series })}
              </h3>
              <div className="artist-gallery-grid">
                {items.map((product) => (
                  <ArtistGalleryFigure
                    key={product.id}
                    product={product}
                    artistId={artist?.id}
                    artistSlug={artist?.slug}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="artist-gallery-grid">
            {products.map((product) => (
              <ArtistGalleryFigure
                key={product.id}
                product={product}
                artistId={artist?.id}
                artistSlug={artist?.slug}
              />
            ))}
          </div>
        )}
      </RevealSection>
      )}

      {/* 3.6 Drops timeline — gated until products carry release_date */}
      {datedDrops.length > 0 && (
        <RevealSection className="artist-drops" aria-label={t('page.drops.sectionLabel')}>
          <h2 className="artist-section-heading">{t('page.drops.heading')}</h2>
          <ol className="artist-drops-list">
            {datedDrops.map((drop) => (
              <li key={drop.id} className="artist-drops-item">
                <span className="artist-drops-marker" aria-hidden="true" />
                <time
                  className="artist-drops-date"
                  dateTime={drop._date.toISOString().slice(0, 10)}
                >
                  {fmtDate(drop._date, locale, {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}
                </time>
                <div className="artist-drops-body">
                  <span className="artist-drops-name">{drop.name}</span>
                  {drop.series && drop.series !== 'N/A' && (
                    <span className="artist-drops-meta">{drop.series}</span>
                  )}
                </div>
                {drop.image && (
                  <img
                    className="artist-drops-thumb"
                    src={productCard(drop.image)}
                    alt={drop.name}
                    loading="lazy"
                  />
                )}
                <span
                  className={`artist-drops-status${
                    drop.isSoldOut ? ' is-soldout' : drop.isPreorder ? ' is-preorder' : ''
                  }`}
                >
                  {drop.isSoldOut ? t('page.drops.soldOut') : drop.isPreorder ? t('page.drops.preorder') : t('page.drops.available')}
                </span>
              </li>
            ))}
          </ol>
        </RevealSection>
      )}

      {/* 3.4 Process strip — gated until process_images is populated */}
      {processImages.length > 0 && (
        <RevealSection className="artist-process" aria-label={t('page.process.sectionLabel')}>
          <h2 className="artist-section-heading">{t('page.process.heading')}</h2>
          <div className="artist-process-strip">
            {processImages.map((src, i) => (
              <figure key={src} className="artist-process-figure">
                <BlurImage
                  src={productDetail(src)}
                  alt={t('page.process.imageAlt', { n: i + 1, name: displayName })}
                  loading="lazy"
                  objectFit="cover"
                  onError={(e) => { e.currentTarget.closest('figure').style.display = 'none'; }}
                />
              </figure>
            ))}
          </div>
        </RevealSection>
      )}

      {/* 3.8 Contact CTA */}
      <ContactSection identifier={identifier} artistId={artist?.id} artistName={displayName} />


    </article>
  );
};

export default ArtistPage;
